import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/DropdownMenu";
import { Button } from "../ui/Button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/Avatar";
import { User, Settings, LogOut, Shield } from "lucide-react";
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from "react-router-dom";

interface UserDropdownProps {
    user: {name?: string; email?: string; role?: string; organizationName?: string};
}

export function UserDropdown({ user }: UserDropdownProps) {
    const { logout } = useAuth();
    const navigate = useNavigate();

    if (!user) {
        return (
            <Button variant="ghost" size="sm" className="w-full justify-start">
                <User className="mr-2 h-4 w-4" />
                Guest User
            </Button>
        );
    }

    const initials = user.name
        ? user.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()
        : user.email?.charAt(0).toUpperCase() || 'U';

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start px-2">
                    <div className="flex items-center gap-2 text-left">
                        <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatar} alt={user.name || user.email} />
                            <AvatarFallback>{initials}</AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col space-y-1">
                            <p className="text-sm font-medium leading-none">
                                {user.name || user.email}
                            </p>
                            <p className="text-xs leading-none text-muted-foreground">
                                {user.role === 'admin' ? 'Administrator' : 
                                 user.role === 'superadmin' ? 'Super Admin' : 'User'}
                            </p>
                        </div>
                    </div>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="start" forceMount>
                <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">
                            {user.name || user.email}
                        </p>
                        <p className="text-xs leading-none text-muted-foreground">
                            {user.email}
                        </p>
                    </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                </DropdownMenuItem>
                {user.role === 'superadmin' && (
                    <DropdownMenuItem onClick={() => {
                        // Trigger the admin panel via custom event
                        window.dispatchEvent(new CustomEvent('sidebar-action', { 
                            detail: { action: 'admin-panel' } 
                        }));
                    }}>
                        <Shield className="mr-2 h-4 w-4" />
                        <span>Unified Admin Panel</span>
                    </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => {
                    await logout('https://estimation.pk');
                }}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
